import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Search, User, X, Building2 } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const Photo = ({ m }) => {
  const [err, setErr] = useState(false);
  if (!m.photo_path || err) return <User className="w-7 h-7 text-muted-foreground" />;
  return <img src={fileUrl(m.photo_path)} alt={`${m.first_name} ${m.last_name}`} loading="lazy" onError={() => setErr(true)} className="w-full h-full object-cover" />;
};

const ClubBand = ({ m, count }) => (
  <Link to={`/skola/${m.club_id}`} className="flex items-center gap-3 px-3 sm:px-4 py-3 bg-primary/10 border-l-4 border-primary hover:bg-primary/20 transition-colors" data-testid={`club-band-${m.club_id}`}>
    <div className="w-11 h-11 border border-primary/60 overflow-hidden bg-muted flex items-center justify-center shrink-0">
      {m.club_logo ? <img src={fileUrl(m.club_logo)} alt={m.club_name} className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-muted-foreground" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-display font-bold text-base sm:text-lg truncate">{m.club_name}</div>
      {m.club_city && <div className="text-xs text-muted-foreground">{m.club_city}</div>}
    </div>
    <span className="text-xs font-semibold text-primary whitespace-nowrap" data-testid={`club-band-count-${m.club_id}`}>{count} izvođača</span>
  </Link>
);

const MemberRow = ({ m }) => (
  <div className="dove-card grid grid-cols-[44px_64px_1fr] sm:grid-cols-[60px_80px_1fr_1fr] gap-3 sm:gap-5 items-center px-3 sm:px-4 py-3 border border-white/10" data-testid={`public-member-${m.id}`}>
    <div className="font-display font-bold text-lg text-primary" data-testid={`public-member-no-${m.id}`}>{String(m.no).padStart(2, "0")}</div>
    <div className="w-16 h-[88px] sm:w-20 sm:h-[110px] border border-primary/60 overflow-hidden bg-muted flex items-center justify-center">
      <Photo m={m} />
    </div>
    <div className="min-w-0">
      <div className="font-semibold text-base sm:text-lg truncate">{m.first_name} {m.last_name}</div>
      <Link to={`/skola/${m.club_id}`} className="sm:hidden text-xs text-primary truncate block">{m.club_name}</Link>
    </div>
    <Link to={`/skola/${m.club_id}`} className="hidden sm:block text-sm text-muted-foreground hover:text-primary transition-colors truncate" data-testid={`public-member-club-${m.id}`}>
      {m.club_name}
    </Link>
  </div>
);

export const AllMembersDirectory = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/public/members").then((r) => {
      const flat = r.data.flatMap((c) => c.members.map((m) => ({ ...m, club_id: c.id, club_name: c.name, club_logo: c.logo_path, club_city: c.city })));
      setAll(flat.map((m, i) => ({ ...m, no: i + 1 })));
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return all;
    return all.filter((m) => norm(`${m.first_name} ${m.last_name} ${m.last_name} ${m.first_name} ${m.club_name}`).includes(t));
  }, [all, q]);

  return (
    <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="all-members-directory">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 border-b-2 border-foreground pb-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Članovi Udruženja</h2>
          <div className="text-sm text-muted-foreground mt-1" data-testid="members-total">Ukupno: <strong className="text-primary">{all.length}</strong></div>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraži člana ili klub..." className="w-full bg-card border border-border focus:border-primary outline-none pl-9 pr-9 py-2.5 text-sm transition-colors" data-testid="members-search-input" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid="members-search-clear"><X className="w-4 h-4" /></button>
          )}
        </div>
      </div>
      <div className="hidden sm:grid grid-cols-[60px_80px_1fr_1fr] gap-5 px-4 pb-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">
        <div>Rb.</div><div>Slika</div><div>Ime i prezime</div><div>Klub</div>
      </div>
      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Učitavanje...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="members-search-empty">Nema rezultata.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m, i) => (
            <div key={m.id} className="space-y-2">
              {(i === 0 || filtered[i - 1].club_id !== m.club_id) && (
                <div className={i === 0 ? "" : "pt-6"}><ClubBand m={m} count={filtered.filter((x) => x.club_id === m.club_id).length} /></div>
              )}
              <MemberRow m={m} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
